import React, { useEffect, useRef, useState } from "react";
import {
  Segment,
  Tab,
  TabPane,
  Header,
  Grid,
  GridColumn,
  Table,
  Dropdown,
  Confirm,
  Icon,
  Label,
  Accordion,
  Modal,
  Button,
  Input,
  Select,
  TextArea,
  Loader,
  Dimmer,
  Progress,
  Placeholder,
} from "semantic-ui-react";
import { Line } from "@ant-design/charts";
import { Pie, Column } from "@ant-design/plots";
import { Link, useLocation } from "react-router-dom";
import {
  Permission,
  Fuelsform,
  Waterform,
  Wasteform,
  Productionform,
  Socialform,
  Energyform,
  Effluentform,
  Dangerousform,
  KPIsform,
  Site,
  AirEmissionsform,
  Form,
  EnvironmentalObjetivesform,
} from "../../../api";
import { useAuth } from "../../../hooks";
import { useLanguage } from "../../../contexts/LanguageContext";
import { apiRetry } from "../../../utils";
import { BasicModal } from "../../Shared";
import {
  energyCodes,
  energyFormulasIndicators,
  energyKPIsCodes,
} from "../../../utils/dataAndFormulas/energyForms";
import {
  effluentCodes,
  effluentFormulasIndicators,
  effluentKPIsCodes,
  convertUnitsFinal as convertUnitsFinalEffluents,
} from "../../../utils/dataAndFormulas/effluentForms";
import {
  waterCodes,
  waterFormulasIndicators,
  waterKPIsCodes,
  convertUnitsFinal as convertUnitsFinalWater,
} from "../../../utils/dataAndFormulas/waterForms";
import {
  wasteCodes,
  wasteFormulasIndicators,
  wasteKPIsCodes,
  convertUnitsFinal as convertUnitsFinalWaste,
} from "../../../utils/dataAndFormulas/wasteForms";
import {
  fuelsCodes,
  fuelsFormulasIndicators,
  fuelsKPIsCodes,
  convertUnitsFinal as convertUnitsFinalFuels,
} from "../../../utils/dataAndFormulas/fuelsForms";
import { convertUnits } from "../../../utils/dataAndFormulas/convert";
import {
  airemissionsCodes,
  airemissionsFormulasIndicators,
  airemissionsKPIsCodes,
  convertAirEmissionsFinal as convertUnitsFinalAir,
} from "../../../utils/dataAndFormulas/airemissionsForms";
import {
  socialCodes,
  socialFormulasIndicators,
  socialKPIsCodes,
} from "../../../utils/dataAndFormulas/socialForms";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateView } from "../../../utils/formatDate";
import { sourcesOptions } from "../../../utils/aspects";
import {
  convertEnergy2,
  convertUnitsFinal,
} from "../../../utils/dataAndFormulas/convertEnergy";

import "./ObjectivesAndBaseline.scss";
import { calculateEmissionsByType, geiCodes, geiKPIsCodes, geiFormulasIndicators } from "../../../utils/dataAndFormulas/GEIForms";

const permissionController = new Permission();
const fuelsController = new Fuelsform();
const waterFormController = new Waterform();
const wasteFormController = new Wasteform();
const productionFormController = new Productionform();
const socialFormController = new Socialform();
const energyFormController = new Energyform();
const effluentController = new Effluentform();
const dangerousController = new Dangerousform();
const kpisController = new KPIsform();
const siteController = new Site();
const airEmissionController = new AirEmissionsform();
const formController = new Form();
const environmentalObjetivesFormController = new EnvironmentalObjetivesform();

// 🎨 Componente de Loading mejorado - Posicionado arriba con mejor diseño
const LoadingOverlay = ({ message, progress, show }) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background:
          "linear-gradient(135deg, rgba(33, 133, 208, 0.95) 0%, rgba(21, 101, 192, 0.95) 100%)",
        padding: "20px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        borderBottom: "3px solid #1678c2",
        animation: "slideDown 0.3s ease-out",
      }}
    >
      <div
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          textAlign: "center",
          color: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "15px",
          }}
        >
          <Loader active inline="centered" size="medium" inverted />
          <div
            style={{
              fontSize: "16px",
              fontWeight: "500",
              color: "#fff",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            {message}
          </div>
        </div>
        {progress !== undefined && (
          <div style={{ marginTop: "15px" }}>
            <Progress
              percent={progress}
              indicating
              size="small"
              style={{
                maxWidth: "500px",
                margin: "0 auto",
              }}
            />
            <div
              style={{
                marginTop: "8px",
                fontSize: "13px",
                color: "#e3f2fd",
                fontWeight: "400",
              }}
            >
              {progress}% completado
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 🎨 Skeleton loader para la tabla
const TableSkeleton = ({ rows = 5, columns = 2 }) => {
  return (
    <Table className="baseline-table" compact size="small">
      <Table.Header>
        <Table.Row>
          {Array.from({ length: columns }).map((_, i) => (
            <Table.HeaderCell key={i}>
              <Placeholder>
                <Placeholder.Line />
              </Placeholder>
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Table.Row key={rowIndex}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Table.Cell key={colIndex}>
                <Placeholder>
                  <Placeholder.Line />
                </Placeholder>
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

export function ObjectivesAndBaseline(props) {
  const { site } = props;
  const {
    user: { role },
    accessToken,
  } = useAuth();

  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [permissionActive, setPermissionsActive] = useState([]);
  const [fielsMain, setFieldsMain] = useState([
    "social",
    "energy",
    "fuels",
    "effluent",
    "waste",
    "water",
    "gei",
    "airEmissions",
  ]);

  const controllerRef = useRef({ abort: false });

  function cancelPendingOperations() {
    if (controllerRef.current) {
      controllerRef.current.abort = true;
    }
    controllerRef.current = { abort: false };
  }

  useEffect(() => {
    (async () => {
      try {
        setPermissionsActive([]);
        if (role) {
          const response = await permissionController.getMyPermissions(accessToken, true);
          setPermissionsActive(response);
        }
      } catch (error) {
        setPermissionsActive([]);
      }
    })();
  }, [role]);

  return (
    <div style={{ width: "100%" }}>
      {site && site !== "undefined" ? (
        <>
          <TableYears
            fielsMain={fielsMain}
            accessToken={accessToken}
            t={t}
            site={site}
            cancelPendingOperations={cancelPendingOperations}
            controllerRef={controllerRef}
          />
        </>
      ) : null}
    </div>
  );
}

function TableYears(props) {
  const {
    fielsMain,
    onReload,
    accessToken,
    year,
    site,
    t,
    cancelPendingOperations,
    controllerRef,
  } = props;

  const [valueBaseline, setValueBaseline] = useState({});
  const [workersTotals, setWorkersTotals] = useState({});
  const [allYears, setAllYears] = useState([]);
  const [hasBaselineError, setHasBaselineError] = useState(false);

  // 🆕 Estados de carga
  const [isLoadingBaseline, setIsLoadingBaseline] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");

  useEffect(() => {
    setValueBaseline({});
    setWorkersTotals({});
    setAllYears([]);
    setHasBaselineError(false);
  }, [site]);

  const ensureBaseDataForYears = async (years = []) => {
    if (years.length === 0) return;

    setIsLoadingBaseline(true);
    setLoadingProgress(0);
    setLoadingMessage("Cargando datos base...");

    const totalSteps = years.length * 2; // workers + baseline por año
    let currentStep = 0;

    // 🔒 Objeto para rastrear qué datos están completos
    const loadedData = {
      workers: {},
      baseline: {},
    };

    for (const year of years) {
      if (hasBaselineError) {
        setIsLoadingBaseline(false);
        return;
      }

      // ============================
      // 1️⃣ Obtener total de workers (OBLIGATORIO)
      // ============================
      if (!workersTotals[year]) {
        setLoadingMessage(`Cargando trabajadores del año ${year}...`);

        try {
          const response = await socialFormController.getTotalWorkersByYears(
            accessToken,
            site,
            [year]
          );

          let totalForYear = 0;

          if (typeof response?.totalWorkers === "number") {
            totalForYear = response.totalWorkers;
          } else if (
            response?.totalWorkers &&
            typeof response.totalWorkers === "object"
          ) {
            totalForYear = Object.values(response.totalWorkers)[0] ?? 0;
          }

          // ⚠️ VALIDACIÓN: Si no hay trabajadores, usar 1 para evitar división por cero
          if (totalForYear === 0) {
            console.warn(
              `⚠️ No hay trabajadores para el año ${year}, usando 1 para evitar división por cero`
            );
            totalForYear = 1;
            toast.warning(
              `${t("no_workers_data_for_year")} ${year}. ${t("calculations_may_not_be_accurate")}`
            );
          }

          setWorkersTotals((prev) => ({
            ...prev,
            [year]: totalForYear,
          }));

          loadedData.workers[year] = true; // ✅ Marcar como cargado
        } catch (error) {
          console.error(`❌ Error cargando trabajadores para ${year}:`, error);
          // ⚠️ En caso de error, usar 1 para evitar bloqueo
          setWorkersTotals((prev) => ({
            ...prev,
            [year]: 1,
          }));
          loadedData.workers[year] = false; // ⚠️ Marcar como fallido
          toast.error(`${t("error_loading_workers_for_year")} ${year}`);
        }

        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      } else {
        loadedData.workers[year] = true; // ✅ Ya estaba cargado
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      // ======================================
      // 2️⃣ Obtener baseline (si no está cargado)
      // ======================================
      if (!valueBaseline[year]) {
        setLoadingMessage(`Calculando línea base del año ${year}...`);

        try {
          const baselineResp = await apiRetry(() =>
            formController.getBaselineByFormBySite(accessToken, site, "production", year)
          );

          const baseVal = baselineResp?.baseline;

          if (!baseVal) {
            toast.warning(`${t("no_baseline_data_for_year")} ${year}.`);
            loadedData.baseline[year] = false;
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
            continue;
          }

          const calculatedValue = await getValueBaseLine(year, baseVal);

          // ⚠️ VALIDACIÓN: Si la baseline es 0, usar 1 para evitar división por cero
          const finalValue = calculatedValue === 0 ? 1 : calculatedValue;
          if (calculatedValue === 0) {
            console.warn(
              `⚠️ Línea base es 0 para el año ${year}, usando 1 para evitar división por cero`
            );
          }

          setValueBaseline((prev) => ({
            ...prev,
            [year]: finalValue,
          }));

          loadedData.baseline[year] = true; // ✅ Marcar como cargado
          currentStep++;
          setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
        } catch (err) {
          const status = err?.status ?? err?.code;

          if (status === 404 || status === 400) {
            loadedData.baseline[year] = false;
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
            continue;
          }

          console.error("❌ Error al obtener línea base:", err);
          toast.error(t("could_not_calculate_baseline"));
          setHasBaselineError(true);
          setIsLoadingBaseline(false);
          return;
        }
      } else {
        loadedData.baseline[year] = true; // ✅ Ya estaba cargado
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }
    }

    // 🔍 Verificar que TODOS los años tengan trabajadores cargados
    const missingWorkers = years.filter((year) => !loadedData.workers[year]);
    if (missingWorkers.length > 0) {
      console.warn(
        `⚠️ Faltan datos de trabajadores para: ${missingWorkers.join(", ")}`
      );
    }

    setLoadingProgress(100);
    setLoadingMessage("¡Datos cargados!");
    setTimeout(() => {
      setIsLoadingBaseline(false);
    }, 500);

    // 📊 Retornar resumen de lo que se cargó
    return loadedData;
  };

  const getValueBaseLine = async (year, type) => {
    try {
      if (type === "VPS") {
        const response1 =
          await productionFormController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_production"
          );
        return response1.total;
      } else {
        const response1 =
          await productionFormController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_billing"
          );
        return response1.total;
      }
    } catch (error) {
      console.error("⚠️ Error obteniendo baseline:", error);
      throw error;
    }
  };

  const panes = fielsMain.map((i, index) => ({
    menuItem: i === "airEmissions"
      ? {
          key: "airEmissions",
          disabled: true,
          content: (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: "6px" }}>
              {t(i)}
              <span style={{ fontSize: "0.65em", background: "#d0d0d0", borderRadius: "4px", padding: "2px 6px", color: "#666", whiteSpace: "nowrap", fontWeight: 600 }}>
                En desarrollo
              </span>
            </span>
          ),
        }
      : t(fielsMain[index]),
    render: () => (
      <>
        {/* <LoadingOverlay
          show={isLoadingBaseline}
          message={loadingMessage}
          progress={loadingProgress}
        /> */}

        <Tab.Pane key={index} style={{ width: "100%", maxWidth: "1800px" }}>
          {!hasBaselineError ? (
            <SubTableYears
              module={i}
              index={index}
              accessToken={accessToken}
              t={t}
              site={site}
              cancelPendingOperations={cancelPendingOperations}
              controllerRef={controllerRef}
              getValueBaseLine={getValueBaseLine}
              ensureBaseDataForYears={ensureBaseDataForYears}
              workersTotals={workersTotals}
              setWorkersTotals={setWorkersTotals}
              valueBaseline={valueBaseline}
              allYears={allYears}
              setAllYears={setAllYears}
            />
          ) : (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#b71c1c",
                fontSize: "1.1rem",
                fontWeight: "500",
              }}
            >
              ⚠️ Ocurrió un error al calcular la línea base.
              <br />
              <button
                onClick={() => {
                  setHasBaselineError(false);
                  onReload?.();
                }}
                style={{
                  marginTop: "15px",
                  padding: "8px 16px",
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Volver a cargar
              </button>
            </div>
          )}
        </Tab.Pane>
      </>
    ),
  }));

  return (
    <div className="baseline-component">
      <Tab
        menu={{ vertical: true, fluid: true }}
        menuPosition="left"
        panes={panes}
      />
    </div>
  );
}

function SubTableYears(props) {
  const {
    module,
    accessToken,
    site,
    t,
    ensureBaseDataForYears,
    workersTotals,
    allYears,
    setAllYears,
    valueBaseline,
  } = props;

  const [totales, setTotales] = useState({});
  const [conceptos, setConceptos] = useState([]);
  const [yearSelected, setYearSelected] = useState("");
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [categoryYears, setCategoryYears] = useState({});
  const [conceptYearSelected, setConceptYearSelected] = useState({});
  const [yearsModule, setYearsModule] = useState([]);

  // 🆕 Estados de carga mejorados
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState(0);
  const [calculationMessage, setCalculationMessage] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  // Solo se pinta si la carga tarda más de 200ms: evita el parpadeo del
  // skeleton en cambios de tab que resuelven rápido (dato ya cacheado en
  // el backend, poco volumen), pero sigue avisando si tarda de verdad.
  // Al cortar apenas termina la carga (sin esperar un mínimo de tiempo
  // visible), tampoco queda un skeleton "flasheando" medio segundo por
  // una carga de 210ms.
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (!isInitialLoading) {
      setShowSkeleton(false);
      return;
    }
    const timer = setTimeout(() => setShowSkeleton(true), 200);
    return () => clearTimeout(timer);
  }, [isInitialLoading]);

  // 🚨 Tracking de datos aproximados/faltantes
  const [dataWarnings, setDataWarnings] = useState({
    missingWorkers: {}, // { 2023: true, 2024: false }
    missingBaseline: {}, // { 2023: false, 2024: true }
  });

  const controllerRef = useRef({ abort: false });

  const cancelPendingOperations = () => {
    if (controllerRef.current) {
      controllerRef.current.abort = true;
    }
    controllerRef.current = { abort: false };
  };

  // 1️⃣ Primer useEffect: carga datos base
  useEffect(() => {
    cancelPendingOperations();
    setIsInitialLoading(true);
    setTotales({});
    setData([]);
    setCategoryYears({});
    setConceptYearSelected({});
    setYearsModule([]);
    setYearSelected("");
    setDataWarnings({ missingWorkers: {}, missingBaseline: {} });

    (async () => {
      try {
        controllerRef.current.abort = false;

        // ✅ SIEMPRE establecer los conceptos primero, independientemente de si existe el formulario
        setConceptos(determineFields(determineKeysKPIs(module)));

        const result = await formController.getFormsBySite(
          accessToken,
          site,
          module
        );

        if (!result || !result[0]) {
          // No hay formulario, pero ya tenemos los conceptos
          setIsInitialLoading(false);
          return;
        }

        const baseForm = result[0];
        const yearsSet = new Set();

        if (baseForm.yearBaseline) {
          yearsSet.add(baseForm.yearBaseline);
          setCategoryYears((prev) => ({
            ...prev,
            [module]: baseForm.yearBaseline,
          }));
        }

        const conceptYears = {};
        for (const field of baseForm.indicators || []) {
          if (field.yearBaseline) {
            conceptYears[field.name] = field.yearBaseline;
            yearsSet.add(field.yearBaseline);
          }
        }

        setConceptYearSelected(conceptYears);
        const detectedYears = Array.from(yearsSet).map((y) => y);
        setYearsModule(detectedYears);

        setAllYears((prev) => {
          const merged = new Set([...(prev || []), ...detectedYears]);
          return Array.from(merged).sort((a, b) => a - b);
        });

        // 🚀 CARGAR DATOS BASE INMEDIATAMENTE después de detectar años
        if (detectedYears.length > 0) {
          console.log("🚀 Cargando datos base para años:", detectedYears);
          await ensureBaseDataForYears(detectedYears);
        }

        setIsInitialLoading(false);
      } catch (error) {
        console.error("❌ Error inicializando datos base:", error);
        setIsInitialLoading(false);
      }
    })();

    return () => cancelPendingOperations();
  }, [module, site]);

  // 2️⃣ Segundo useEffect: calcula cuando todo está listo
  // 🔒 COMENTADO: Cálculo automático por cada KPI deshabilitado
  /*
  useEffect(() => {
    (async () => {
      if (!yearsModule || yearsModule.length === 0) return;

      // ⏸️ ESPERAR a que los datos base se carguen primero
      // Si yearsModule cambió pero aún no tenemos los datos base, no hacer nada
      const needsBaseData = yearsModule.some(
        (year) => !workersTotals[year] && !valueBaseline[year]
      );

      if (needsBaseData) {
        console.log("⏸️ Esperando que se carguen los datos base...");
        return; // Salir sin calcular, esperará al siguiente render
      }

      setIsCalculating(true);
      setCalculationProgress(0);
      setCalculationMessage("Preparando cálculos...");

      // 🔍 Verificar que tengamos lo mínimo necesario
      const missingWorkers = yearsModule.filter(
        (year) => !workersTotals[year] || workersTotals[year] === 0
      );

      if (missingWorkers.length > 0) {
        console.error(
          `❌ Faltan trabajadores para los años: ${missingWorkers.join(", ")}`
        );
        toast.error(
          `${t("cannot_calculate_indicators_missing_workers")} ${missingWorkers.join(
            ", "
          )}`,
          {
            autoClose: 5000,
          }
        );
        setIsCalculating(false);
        return; // ⛔ Detener si faltan trabajadores
      }

      const moduleResults = {};
      const totalYears = yearsModule.length;

      for (let i = 0; i < yearsModule.length; i++) {
        const year = yearsModule[i];

        if (controllerRef.current.abort) break;

        setCalculationMessage(`Procesando año ${year}...`);
        setCalculationProgress(Math.round((i / totalYears) * 100));

        const responseData = await fetchFormDataForYear(year);
        if (!responseData) continue;

        const totals = await calculateTotalsByYear(responseData);

        // ✅ Solo aplicar fórmulas si tenemos trabajadores
        if (workersTotals[year] && workersTotals[year] > 0) {
          const processed = await applyIndicatorFormulas(totals, year);

          Object.keys(processed).forEach((concepto) => {
            if (!moduleResults[concepto]) moduleResults[concepto] = {};

            const yearData = processed?.[concepto]?.[year];
            if (yearData && typeof yearData.total === "number") {
              moduleResults[concepto][year] = {
                total: Number(yearData.total.toFixed(3)),
                unit: yearData.unit,
              };
            }
          });
        } else {
          console.warn(
            `⚠️ Saltando cálculo de indicadores para ${year} - sin trabajadores`
          );
        }
      }

      setTotales(moduleResults);
      setCalculationProgress(100);
      setCalculationMessage("¡Cálculos completados!");

      setTimeout(() => {
        setIsCalculating(false);
      }, 500);
    })();
  }, [yearsModule, workersTotals, valueBaseline]); // ✅ Agregar dependencias
  */

  // 🔒 MODIFICADO: Solo actualiza años sin calcular valores
  useEffect(() => {
    (async () => {
      if (!site || yearSelected === "" || !conceptos) return;

      setIsCalculating(true);
      setCalculationMessage(`Actualizando año de línea base a ${yearSelected}...`);

      try {
        // Para módulo GEI, siempre permitir guardar el año sin verificar datos
        if (module === "gei") {
          await updateFormWithIndicators(yearSelected);
          toast.success(t("baseline_year_updated_successfully"));
        } else {
          // Para otros módulos, verificar si existen datos para el año
          const response = await fetchFormDataForYear(yearSelected);

          if (response !== null) {
            // Solo actualizar el formulario con los años, sin cálculos
            await updateFormWithIndicators(yearSelected);
            toast.success(t("baseline_year_updated_successfully"));
          } else {
            setModalOpen(true);
          }
        }

        setAllYears((prev) => {
          const updated = new Set(prev || []);
          updated.add(yearSelected);
          return Array.from(updated);
        });
      } catch (error) {
        console.error("❌ Error en actualización por año:", error);
        toast.error(t("error_updating_baseline_year"));
      } finally {
        setIsCalculating(false);
      }
    })();
  }, [yearSelected]);

  const handleCategoryYearChange =
    (category) =>
    (e, { value }) => {
      setCategoryYears((prev) => {
        const updatedYears = { ...prev, [category]: value };
        setYearSelected(value);
        return updatedYears;
      });
    };

  // 🔒 MODIFICADO: Solo actualiza el año sin calcular valores
  const handleConceptYearChange =
    (concept) =>
    async (e, { value }) => {
      setIsCalculating(true);
      setCalculationMessage(`Actualizando año de línea base para ${t(concept)}...`);

      try {
        // Actualizar el estado local
        setConceptYearSelected((prev) => ({
          ...prev,
          [concept]: value,
        }));

        // Para módulo GEI, siempre permitir guardar el año sin verificar datos
        if (module === "gei") {
          await updateFormWithYear(concept, value, true);
        } else {
          // Para otros módulos, verificar que existan datos
          const formData = await fetchFormDataForYear(value);

          if (formData !== null) {
            // Solo actualizar el formulario con el año, sin cálculos
            await updateFormWithYear(concept, value, true);
          } else {
            setModalOpen(true);
          }
        }
      } catch (error) {
        console.error("Error actualizando concepto:", error);
        toast.error(t("error_updating_baseline_year"));
      } finally {
        setIsCalculating(false);
      }
    };

  const setValuesYear = () => {
    setCategoryYears((prev) => {
      const updatedYears = { ...prev, [module]: "" };
      setYearSelected("");
      return updatedYears;
    });
  };

  const determineKeysKPIs = (a) => {
    switch (a) {
      case "energy":
        return energyKPIsCodes;
      case "fuels":
        return fuelsKPIsCodes;
      case "effluent":
        return effluentKPIsCodes;
      case "water":
        return waterKPIsCodes;
      case "waste":
        return wasteKPIsCodes;
      case "social":
        return socialKPIsCodes;
      case "airEmissions":
        return airemissionsKPIsCodes;
      case "gei":
        return geiKPIsCodes;
      default:
        return null;
    }
  };

  const determineKeysFields = (a) => {
    switch (a) {
      case "energy":
        return energyCodes;
      case "fuels":
        return fuelsCodes;
      case "effluent":
        return effluentCodes;
      case "water":
        return waterCodes;
      case "waste":
        return wasteCodes;
      case "social":
        return socialCodes;
      case "airEmissions":
        return airemissionsCodes;
              case "gei":
        return geiCodes;
      default:
        return null;
    }
  };

  const determineFormulasIndicators = (a) => {
    switch (a) {
      case "energy":
        return energyFormulasIndicators;
      case "fuels":
        return fuelsFormulasIndicators;
      case "effluent":
        return effluentFormulasIndicators;
      case "water":
        return waterFormulasIndicators;
      case "waste":
        return wasteFormulasIndicators;
      case "social":
        return socialFormulasIndicators;
      case "airEmissions":
        return airemissionsFormulasIndicators;
              case "gei":
        return calculateEmissionsByType;
      default:
        return null;
    }
  };

  const determineFields = (data) => {
    const fields = new Set();
    data &&
      Object.keys(data).forEach((key) => {
        fields.add(key);
      });
    return Array.from(fields);
  };

  // Setea el mismo año base para TODOS los conceptos (y el yearBaseline a
  // nivel formulario) de forma atómica en el servidor — crea el formulario
  // si todavía no existe, ver upsertFormIndicators en el controlador.
  const updateFormWithIndicators = async (year) => {
    try {
      const updatedConceptsYears = {};
      conceptos.forEach((concept) => {
        updatedConceptsYears[concept] = year;
      });
      setConceptYearSelected(updatedConceptsYears);

      const response = await formController.upsertFormIndicators(
        accessToken,
        module,
        site,
        {
          yearBaseline: year,
          indicators: conceptos.map((concept) => ({
            name: concept,
            yearBaseline: year,
          })),
        }
      );
      if (response && response.code === 200) {
        toast.success(t("form_updated_successfully"));
      }
    } catch (error) {
      console.error("Error updating form with indicators:", error);
      toast.error(t("error_updating_form"));
    }
  };

  // 🔥 CACHÉ GLOBAL de unidades para evitar llamadas repetidas
  const unitsCacheRef = useRef({});

  // 🧹 Limpiar caché cuando cambia el módulo
  useEffect(() => {
    return () => {
      unitsCacheRef.current = {};
    };
  }, [module]);

  const calculateTotalsByYear = async (data) => {
    const totalesPorAño = {};
    const conceptosSet = [];
    const fields = determineFields(determineKeysFields(module));

    if (data && data.length > 0) {
      // 📊 Agrupar datos por año para una sola llamada por año
      const dataByYear = {};
      data.forEach((item) => {
        const año = item.year;
        if (!dataByYear[año]) {
          dataByYear[año] = [];
        }
        dataByYear[año].push(item);
      });

      // 🚀 Obtener unidades solo UNA VEZ por año
      const yearsToFetch = Object.keys(dataByYear);
      for (const año of yearsToFetch) {
        // ✅ Verificar si ya tenemos en caché las unidades para este año
        const cacheKey = `${module}-${site}-${año}`;

        if (!unitsCacheRef.current[cacheKey]) {
          // 📥 Solo llamar a la API si NO está en caché
          unitsCacheRef.current[cacheKey] = await unitsModule(
            module,
            site,
            año,
            fields
          );
          console.log(`✅ Unidades cargadas para ${año} (${module})`);
        } else {
          console.log(`♻️ Usando caché para ${año} (${module})`);
        }
      }

      // 🔄 Procesar todos los datos usando el caché
      data.forEach((item) => {
        const año = item.year;
        const cacheKey = `${module}-${site}-${año}`;
        const units = unitsCacheRef.current[cacheKey];

        fields.forEach((concepto) => {
          if (item[concepto]) {
            if (!conceptosSet.includes(concepto)) {
              conceptosSet.push(concepto);
            }

            if (!totalesPorAño[concepto]) {
              totalesPorAño[concepto] = {};
            }

            if (!totalesPorAño[concepto][año]) {
              totalesPorAño[concepto][año] = {
                total: 0,
                count: 0,
                unit: units.fields[concepto] || item[concepto]?.unit || "",
              };
            }

            totalesPorAño[concepto][año].total += item[concepto].value
              ? convertUnitsModule(
                  item[concepto].value,
                  item[concepto].unit,
                  totalesPorAño[concepto][año].unit,
                  concepto,
                  module
                )
              : 0;

            totalesPorAño[concepto][año].count += 1;
          }
        });
      });
    }

    // 📈 Calcular promedios
    Object.keys(totalesPorAño).forEach((concept) => {
      Object.keys(totalesPorAño[concept]).forEach((year) => {
        const { total, count } = totalesPorAño[concept][year];
        if (count > 0) {
          totalesPorAño[concept][year].average = total / count;
        }
      });
    });

    return totalesPorAño;
  };

  const applyIndicatorFormulas = (totalesPorAño, year) => {
    const updatedTotales = { ...totalesPorAño };
    const conceptosSet = [];

    if (updatedTotales !== undefined) {
      // 🔒 VALIDACIÓN CRÍTICA: Verificar que existan los datos necesarios
      const valueB = valueBaseline[year];
      const workersTotal = workersTotals[year];

      let usedApproximateWorkers = false;
      let usedApproximateBaseline = false;

      // ⚠️ Validar baseline
      if (valueB === undefined || valueB === null) {
        console.warn(
          `⚠️ No hay valor de baseline para el año ${year}, usando 1`
        );
        usedApproximateBaseline = true;
      }

      // ⚠️ CRÍTICO: Validar trabajadores
      if (
        workersTotal === undefined ||
        workersTotal === null ||
        workersTotal === 0
      ) {
        console.error(`❌ No hay datos de trabajadores para el año ${year}`);
        toast.error(
          `${t("cannot_calculate_indicators_for_year")} ${year}`,
          {
            autoClose: 5000,
          }
        );
        // ⛔ NO calcular si no hay trabajadores - retornar vacío
        return updatedTotales;
      }

      // ✅ Usar valores seguros (nunca 0 para evitar división por cero)
      const safeValueB = valueB === 0 || !valueB ? 1 : valueB;
      const safeWorkers =
        workersTotal === 0 || !workersTotal ? 1 : workersTotal;

      // 🚨 Registrar si se usaron valores aproximados
      if (valueB === 0 || !valueB) {
        usedApproximateBaseline = true;
      }
      if (workersTotal === 0 || !workersTotal) {
        usedApproximateWorkers = true;
      }

      // 📊 Actualizar warnings para mostrar en UI
      setDataWarnings((prev) => ({
        ...prev,
        missingWorkers: {
          ...prev.missingWorkers,
          [year]: usedApproximateWorkers,
        },
        missingBaseline: {
          ...prev.missingBaseline,
          [year]: usedApproximateBaseline,
        },
      }));

      const fields = determineFields(determineKeysKPIs(module));

      fields.forEach((concepto) => {
        if (!updatedTotales[concepto]) {
          updatedTotales[concepto] = {};
          conceptosSet.push(concepto);
        }

        if (!updatedTotales[concepto][year]) {
          updatedTotales[concepto][year] = {};
        }

        updatedTotales[concepto][year] = determineFormulasIndicators(module)(
          concepto,
          year,
          updatedTotales,
          safeValueB,
          safeWorkers
        );
      });
    }

    return updatedTotales;
  };

  const fetchFormDataForYear = async (year) => {
    if (data.hasOwnProperty(year)) {
      return data[year];
    } else {
      const formData = await moduleConsult(module, year);
      setData((prevData) => ({ ...prevData, [year]: formData }));
      return formData;
    }
  };

  const updateTotales = async (concept, year, result) => {
    if (result && result[concept] && result[concept][year]) {
      setTotales((prevTotales) => {
        const updatedTotales = {
          ...prevTotales,
          [concept]: {
            ...prevTotales[concept],
            [year]: result[concept][year],
          },
        };
        return updatedTotales;
      });
    }
  };

  const fetchFormData = async (year) => {
    try {
      const formData = await fetchFormDataForYear(year);
      if (formData !== null) {
        const transformedData = await calculateTotalsByYear(formData);
        const finalResult = applyIndicatorFormulas(transformedData, year);

        let listConcepts = determineFields(determineKeysKPIs(module));
        for (const c of listConcepts) {
          if (!totales[c] || (totales[c] && !totales[c][year])) {
            await updateTotales(c, year, finalResult);
          }
        }
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // 🔒 COMENTADO: Función que calculaba valores, ya no se usa
  /*
  const fetchAndUpdateFormData = async (year) => {
    try {
      const formData = await fetchFormDataForYear(year);
      if (formData !== null) {
        const transformedData = await calculateTotalsByYear(formData);
        const finalResult = applyIndicatorFormulas(transformedData, year);

        const listConcepts = determineFields(determineKeysKPIs(module));
        const results = [];

        for (const c of listConcepts) {
          if (!totales[c] || !totales[c][year]) {
            await updateTotales(c, year, finalResult);
            const ok = await updateFormWithYear(c, year, false);
            results.push(ok);
          }
        }

        if (results.length === 0) {
          toast.info(t("year_data_already_updated"));
        } else if (results.every((r) => r === true)) {
          toast.success(t("year_data_updated_successfully"));
        } else {
          toast.warning(t("some_forms_could_not_update"));
        }
        return true;
      }

      toast.warning(t("no_data_found_for_selected_year"));
      return false;
    } catch (error) {
      console.error("Error fetching form data:", error);
      toast.error(t("error_updating_year_data"));
      return false;
    }
  };
  */

  // 🔒 COMENTADO: Función que calculaba valores por concepto, ya no se usa
  /*
  const fetchAndUpdateConceptFormData = async (year, concept) => {
    try {
      const formData = await fetchFormDataForYear(year);

      if (formData !== null) {
        const transformedData = await calculateTotalsByYear(formData);
        const finalResult = applyIndicatorFormulas(transformedData, year);
        await updateTotales(concept, year, finalResult);
        await updateFormWithYear(concept, year);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };
  */

  function convertUnitsModule(value, fromUnit, toUnit, material, module) {
    switch (module) {
      case "social":
        return convertUnits(value, fromUnit, toUnit);
      case "energy":
        return convertUnitsFinal(value, fromUnit, toUnit, material);
      case "production":
        return convertUnits(value, fromUnit, toUnit);
      case "effluent":
        return convertUnitsFinalEffluents(value, fromUnit, toUnit, material);
      case "water":
        return convertUnitsFinalWater(value, fromUnit, toUnit, material);
      case "waste":
        return convertUnitsFinalWaste(value, fromUnit, toUnit, material);
      case "airemissions":
      case "airEmissions":
        return convertUnitsFinalAir(value, fromUnit, toUnit, material);
      case "fuels":
        return convertUnitsFinal(value, fromUnit, toUnit, material);
      case "gei":
        return convertUnitsFinal(value, fromUnit, toUnit, material);
      default:
        return null;
    }
  }

  const unitsModule = async (module, site, año, fields) => {
    switch (module) {
      case "social":
        return await socialFormController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "energy":
        return await energyFormController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "production":
        return await productionFormController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "effluent":
        return await effluentController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "water":
        return await waterFormController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "waste":
        return await wasteFormController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "airemissions":
      case "airEmissions":
        return await airEmissionController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "fuels":
        return await fuelsController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
      case "gei":
        // GEI usa las unidades combinadas de energía y fuels
        const energyUnits = await energyFormController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
        const fuelsUnits = await fuelsController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );
        return {
          fields: {
            ...energyUnits?.fields,
            ...fuelsUnits?.fields,
          }
        };
      default:
        return null;
    }
  };

  const moduleConsult = (a, year) => {
    switch (a) {
      case "social":
        return getSocialForm(year);
      case "energy":
        return getEnergyForm(year);
      case "production":
        return getProductionForm(year);
      case "effluent":
        return getEffluentForm(year);
      case "water":
        return getWaterForm(year);
      case "waste":
        return getWasteForm(year);
      case "kpis":
        return getKPIsForm(year);
      case "dangerous":
        return getDangerousForm(year);
      case "airemissions":
      case "airEmissions":
        return getAirEmissionForm(year);
      case "fuels":
        return getFuelsForm(year);
      case "gei":
        return getGEIForm(year);
      default:
        return null;
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) =>
    (currentYear - i).toString()
  );

  const getWaterForm = async (year) => {
    const response = await waterFormController.getWaterFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.waterForms && response.waterForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.waterForms;
      }
    } else {
      return null;
    }
  };

  const getProductionForm = async (year) => {
    const response =
      await productionFormController.getProductionFormsBySiteAndYear(
        accessToken,
        site,
        year
      );
    if (response.code === 200) {
      if (response.productionForms && response.productionForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.productionForms;
      }
    } else {
      return null;
    }
  };

  const getSocialForm = async (year) => {
    const response = await socialFormController.getSocialFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.socialForms && response.socialForms.length === 0) {
        setModalOpen(true);
        setValuesYear();
        return null;
      } else {
        return response.socialForms;
      }
    } else {
      return null;
    }
  };

  const getEnergyForm = async (year) => {
    const response = await energyFormController.getEnergyFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.energyForms && response.energyForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.energyForms;
      }
    } else {
      return null;
    }
  };

  const getDangerousForm = async (year) => {
    const response = await dangerousController.getDangerousFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.dangerousForms && response.dangerousForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.dangerousForms;
      }
    } else {
      return null;
    }
  };

  const getEffluentForm = async (year) => {
    const response = await effluentController.getEffluentFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.effluentForms && response.effluentForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.effluentForms;
      }
    } else {
      return null;
    }
  };

  const getKPIsForm = async (year) => {
    const response = await kpisController.getKPIsFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.kpisForms && response.kpisForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.kpisForms;
      }
    } else {
      return null;
    }
  };

  const getWasteForm = async (year) => {
    const response = await wasteFormController.getWasteFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.wasteForms && response.wasteForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.wasteForms;
      }
    } else {
      return null;
    }
  };

  const getAirEmissionForm = async (year) => {
    const response =
      await airEmissionController.getAirEmissionsFormsBySiteAndYear(
        accessToken,
        site,
        year
      );
    if (response.code === 200) {
      if (
        response.airEmissionsForms &&
        response.airEmissionsForms.length === 0
      ) {
        setModalOpen(true);
        setValuesYear();
        return null;
      } else {
        return response.airEmissionsForms;
      }
    } else {
      return null;
    }
  };

  const getFuelsForm = async (year) => {
    const response = await fuelsController.getFuelsFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    if (response.code === 200) {
      if (response.fuelsForms && response.fuelsForms.length === 0) {
        setModalOpen(true);
        return null;
      } else {
        return response.fuelsForms;
      }
    } else {
      return null;
    }
  };

  const getGEIForm = async (year) => {
    // GEI usa los mismos datos de energía y fuels
    const energyResponse = await energyFormController.getEnergyFormsBySiteAndYear(
      accessToken,
      site,
      year
    );
    const fuelsResponse = await fuelsController.getFuelsFormsBySiteAndYear(
      accessToken,
      site,
      year
    );

    let combinedForms = [];

    if (energyResponse.code === 200 && energyResponse.energyForms) {
      combinedForms = [...combinedForms, ...energyResponse.energyForms];
    }

    if (fuelsResponse.code === 200 && fuelsResponse.fuelsForms) {
      combinedForms = [...combinedForms, ...fuelsResponse.fuelsForms];
    }

    if (combinedForms.length === 0) {
      setModalOpen(true);
      return null;
    }

    return combinedForms;
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  // Actualiza (o crea) el año base de UN concepto de forma atómica en el
  // servidor — evita que dos conceptos guardados casi al mismo tiempo desde
  // dos pestañas/usuarios se pisen entre sí, que era lo que pasaba antes al
  // leer el documento entero, modificarlo en memoria y volver a guardarlo.
  const updateFormWithYear = async (concept, year, showToast = true) => {
    try {
      const response = await formController.upsertFormIndicators(
        accessToken,
        module,
        site,
        { indicators: [{ name: concept, yearBaseline: year }] }
      );
      if (response?.code === 200) {
        if (showToast) toast.success(t("form_updated_successfully"));
        return true;
      } else {
        if (showToast) toast.error(t("error_updating_form"));
        return false;
      }
    } catch (error) {
      console.error("Error updating or creating form:", error);
      if (showToast)
        toast.error(t("error_occurred_updating_form"));
      return false;
    }
  };

  function showWithTwoDecimals(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }

    if (numero % 1 === 0) {
      return numero.toString();
    } else {
      let numeroConCuatroDecimales = numero.toFixed(2);
      return parseFloat(numeroConCuatroDecimales).toString();
    }
  }

  // `showSkeleton` (no `isInitialLoading` directo) — ver el useEffect que
  // lo arma más arriba: se retrasa 200ms para no parpadear en cambios de
  // tab que resuelven rápido.
  if (showSkeleton) {
    return <TableSkeleton rows={5} columns={3} />;
  }
  return (
    <>
      {/* <LoadingOverlay
        show={isCalculating}
        message={calculationMessage}
        progress={calculationProgress}
      /> */}

      <div
        style={{
          opacity: isCalculating ? 0.6 : 1,
          transition: "opacity 0.3s ease",
          pointerEvents: isCalculating ? "none" : "auto",
          minHeight: "400px",
        }}
      >
        {/* 🚨 Panel de Advertencias */}
        {Object.keys(dataWarnings.missingWorkers).some(
          (year) => dataWarnings.missingWorkers[year]
        ) ||
        Object.keys(dataWarnings.missingBaseline).some(
          (year) => dataWarnings.missingBaseline[year]
        ) ? (
          <Segment
            color="orange"
            style={{
              marginBottom: "15px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
            }}
          >
            <Header as="h4" style={{ margin: 0, color: "#856404" }}>
              <Icon name="warning sign" />
              Datos Aproximados en Uso
            </Header>
            <div
              style={{ marginTop: "10px", fontSize: "14px", color: "#856404" }}
            >
              {Object.keys(dataWarnings.missingWorkers).filter(
                (year) => dataWarnings.missingWorkers[year]
              ).length > 0 && (
                <div style={{ marginBottom: "5px" }}>
                  <strong>Sin datos de trabajadores:</strong>{" "}
                  {Object.keys(dataWarnings.missingWorkers)
                    .filter((year) => dataWarnings.missingWorkers[year])
                    .join(", ")}{" "}
                  - Se usó valor 1 para cálculos.
                </div>
              )}
              {Object.keys(dataWarnings.missingBaseline).filter(
                (year) => dataWarnings.missingBaseline[year]
              ).length > 0 && (
                <div>
                  <strong>Sin línea base:</strong>{" "}
                  {Object.keys(dataWarnings.missingBaseline)
                    .filter((year) => dataWarnings.missingBaseline[year])
                    .join(", ")}{" "}
                  - Se usó valor 1 para cálculos.
                </div>
              )}
            </div>
          </Segment>
        ) : null}

        <Table
          className="baseline-table"
          compact
          size="small"
          style={{ width: "100%", maxWidth: "1200px" }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("indicators")}</Table.HeaderCell>
              <Table.HeaderCell>
                <Dropdown
                  text={<strong>{t("year")}</strong>}
                  placeholder={t("select")}
                  options={years.map((ds) => ({
                    key: ds,
                    text: ds,
                    value: ds,
                  }))}
                  selection
                  onChange={handleCategoryYearChange(module)}
                  value={categoryYears[module]}
                  style={{ width: "100%" }}
                  size="tiny"
                  disabled={isCalculating}
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {conceptos.map((item, index) => (
              <Table.Row key={index} style={{ position: "relative", zIndex: conceptos.length - index }}>
                <Table.Cell style={{ width: "60%" }}>{t(item)}</Table.Cell>
                <Table.Cell style={{ width: "40%" }}>
                  <Dropdown
                    placeholder={t("select")}
                    options={years.map((ds) => ({
                      key: ds,
                      text: ds,
                      value: ds,
                    }))}
                    selection
                    onChange={handleConceptYearChange(item)}
                    value={conceptYearSelected[item] || ""}
                    style={{ width: "100%" }}
                    disabled={isCalculating}
                    upward={index >= Math.floor(conceptos.length / 2)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {/* {["energy", "water", "fuels", "waste", "effluent"].includes(module) ? (
          <div style={{ width: "100%", marginTop: 20, marginLeft: 10 }}>
            <Header as="h3">{t("objectives")}</Header>
            <ObjectivesTable
              yearSelected={yearSelected}
              aspectSelected={module}
              accessToken={accessToken}
              site={site}
              allowAdd={true}
              t={t}
              conceptYearSelected={conceptYearSelected}
            />
          </div>
        ) : null} */}
      </div>

      <Modal open={modalOpen} onClose={closeModal} size="tiny">
        <Modal.Header>No hay datos cargados</Modal.Header>
        <Modal.Content>
          <p>
            No existen datos disponibles para el año seleccionado en {t(module)}
            . Por favor, cargue los datos correspondientes o seleccione otro
            año.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button color="green" onClick={closeModal}>
            Cerrar
          </Button>
        </Modal.Actions>
      </Modal>

      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}

const ObjectivesTable = ({
  yearSelected,
  conceptYearSelected,
  aspectSelected,
  accessToken,
  site,
  allowAdd = true,
  t,
}) => {
  const [objectives, setObjectives] = useState([]);
  const [newObjective, setNewObjective] = useState({
    source: "",
    measure: "",
    impact: "",
    investment: "",
    reductionTons: "",
  });

  useEffect(() => {
    if (!conceptYearSelected || !aspectSelected) return;

    (async () => {
      try {
        const yearsWithConcepts = Array.from(
          new Set(Object.values(conceptYearSelected))
        );
        let allObjectives = [];

        for (const year of yearsWithConcepts) {
          const response =
            await environmentalObjetivesFormController.getEnvironmentalObjectivesByYearAndAspect(
              accessToken,
              site,
              year,
              aspectSelected
            );
          if (response && response.code === 200) {
            allObjectives = allObjectives.concat(response.objetives || []);
          }
        }

        setObjectives(allObjectives);
      } catch (error) {
        console.error("Error fetching objectives:", error);
        setObjectives([]);
      }
    })();
  }, [conceptYearSelected, aspectSelected]);

  const addObjective = async () => {
    if (!newObjective.year || !aspectSelected) {
      toast.error(t("must_select_year_and_aspect"));
      return;
    }
    try {
      const impact = parseFloat(newObjective.impact || 0);
      const year = newObjective.year;
      const aspect = aspectSelected;

      const existeImpacto100 = objectives.some(
        (obj) =>
          obj.year === year &&
          obj.aspect === aspect &&
          parseFloat(obj.impact) === 100
      );
      if (existeImpacto100) {
        toast.error(t("impact_100_exists"));
        return;
      }

      const totalImpact = objectives
        .filter((obj) => obj.year === year && obj.aspect === aspect)
        .reduce((sum, obj) => sum + parseFloat(obj.impact || 0), 0);

      if (totalImpact + impact > 100) {
        toast.error(
          `${t("impact_sum_exceeds_100")} (actual: ${totalImpact}%).`
        );
        return;
      }

      const newData = { ...newObjective, aspect: aspectSelected, site: site };
      const response =
        await environmentalObjetivesFormController.createEnvironmentalObjetivesForm(
          accessToken,
          newData
        );
      if (response && response.code === 200) {
        toast.success(t("objective_created"));
        setObjectives([
          ...objectives,
          { ...newObjective, aspect: aspectSelected },
        ]);
        setNewObjective({
          source: "",
          measure: "",
          impact: "",
          investment: "",
          reductionTons: "",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_creating_objective"));
    }
  };

  const deleteObjective = async (idx) => {
    const obj = objectives[idx];
    try {
      const response =
        await environmentalObjetivesFormController.deleteEnvironmentalObjetivesForm(
          accessToken,
          obj.id
        );
      if (response && response.code === 200) {
        setObjectives(objectives.filter((_, i) => i !== idx));
        toast.success(t("objective_deleted"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_deleting_objective"));
    }
  };

  const aspectosOptions = [
    { key: "energy", text: t("energy"), value: "energy" },
    { key: "water", text: t("water"), value: "water" },
    { key: "waste", text: t("waste"), value: "waste" },
    { key: "effluent", text: t("effluent"), value: "effluent" },
    { key: "fuels", text: t("fuels"), value: "fuels" },
  ];

  const accionOptions = [
    { key: "reduce", text: t("reduce"), value: "reduce" },
    { key: "increase", text: t("increase"), value: "increase" },
  ];

  const startYear = 2020;
  const endYear = currentYear + 25;

  const yearsRange = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const yearsOptions = yearsRange.map((year) => ({
    key: year,
    text: year.toString(),
    value: year,
  }));

  return (
    <Table
      celled
      striped
      compact
      size="small"
      style={{
        width: "100%",
        tableLayout: "fixed",
        wordWrap: "break-word",
      }}
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("year")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("action")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("source")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("reductionTons")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("start")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("end")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "20%" }}>
            {t("measure")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("impact")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>
            {t("investment")}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ width: "10%" }}>{t("")}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {objectives && objectives.length > 0 ? (
          objectives.map((obj, idx) => (
            <Table.Row key={idx}>
              <Table.Cell>{obj.year}</Table.Cell>
              <Table.Cell>
                {accionOptions.find((opt) => opt.value === obj.action)?.text ||
                  obj.action}
              </Table.Cell>
              <Table.Cell>{obj.source}</Table.Cell>
              <Table.Cell>{obj.reductionTons}</Table.Cell>
              <Table.Cell>{formatDateView(obj.periodStart)}</Table.Cell>
              <Table.Cell>{formatDateView(obj.periodEnd)}</Table.Cell>
              <Table.Cell>{obj.measure}</Table.Cell>
              <Table.Cell>{obj.impact}</Table.Cell>
              <Table.Cell>{obj.investment}</Table.Cell>
              <Table.Cell>
                <Button
                  icon="trash"
                  size="tiny"
                  color="red"
                  onClick={() => deleteObjective(idx)}
                />
              </Table.Cell>
            </Table.Row>
          ))
        ) : (
          <Table.Row>
            <Table.Cell colSpan={6} textAlign="center">
              No hay objetivos disponibles
            </Table.Cell>
          </Table.Row>
        )}

        {allowAdd && (
          <Table.Row>
            <Table.Cell style={{ width: "10%" }}>
              <Select
                fluid
                options={yearsOptions}
                name="year"
                placeholder={t("year")}
                value={newObjective.year}
                onChange={(_, { value }) =>
                  setNewObjective({ ...newObjective, year: value })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Dropdown
                fluid
                selection
                placeholder={t("select_action")}
                options={accionOptions}
                value={newObjective.action}
                onChange={(e, { value }) =>
                  setNewObjective({ ...newObjective, action: value })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Select
                fluid
                options={sourcesOptions[aspectSelected] || []}
                name="source"
                placeholder={t("source")}
                value={newObjective.source}
                onChange={(_, { value }) =>
                  setNewObjective({ ...newObjective, source: value })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Input
                fluid
                type="number"
                min={1}
                max={100}
                placeholder={t("reduction_lbo")}
                value={newObjective.reductionTons}
                onChange={(e) =>
                  setNewObjective({
                    ...newObjective,
                    reductionTons: e.target.value,
                  })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Input
                fluid
                type="date"
                value={newObjective.periodStart}
                onChange={(e) =>
                  setNewObjective({
                    ...newObjective,
                    periodStart: e.target.value,
                  })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Input
                fluid
                type="date"
                value={newObjective.periodEnd}
                onChange={(e) =>
                  setNewObjective({
                    ...newObjective,
                    periodEnd: e.target.value,
                  })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <TextArea
                fluid
                placeholder={t("measure_placeholder")}
                value={newObjective.measure}
                onChange={(e) =>
                  setNewObjective({ ...newObjective, measure: e.target.value })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Input
                fluid
                placeholder={t("impact_placeholder")}
                type="number"
                step={0.5}
                min={0.5}
                max={100}
                value={newObjective.impact}
                onChange={(e) =>
                  setNewObjective({ ...newObjective, impact: e.target.value })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Input
                fluid
                placeholder={t("investment_placeholder")}
                type="number"
                value={newObjective.investment}
                onChange={(e) =>
                  setNewObjective({
                    ...newObjective,
                    investment: e.target.value,
                  })
                }
              />
            </Table.Cell>

            <Table.Cell style={{ width: "10%" }}>
              <Button
                icon="plus"
                color="green"
                size="tiny"
                onClick={addObjective}
                fluid
              />
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
};
